import { memo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useGetDepartmentByIdQuery, useRemoveStaffFromDepartmentMutation, useDeleteDepartmentMutation } from "../../../service/department-service";
import Button from "../../../common/components/xepp/button/Button";
import styles from "./DepartmentDetailPage.module.scss";
import { ApplicationRoutes } from "../../../routes";
import AddMembersModal from "./components/AddMembersModal";
import { toast } from "react-toastify";
import ConfirmationModal from "../../../common/components/xepp/confirmation-modal/ConfirmationModal";
import Can from "../../../guards/Can";
import { UtilityPermissions } from "../../../model/permissions/permissions";

const DepartmentDetailPage = memo(() => {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { data: department, isLoading, error, refetch } = useGetDepartmentByIdQuery(id || "");
	const [removeStaff, { isLoading: isRemoving }] = useRemoveStaffFromDepartmentMutation();
	const [isAddMembersModalOpen, setIsAddMembersModalOpen] = useState(false);
	const [deleteDepartment, { isLoading: isDeleting }] = useDeleteDepartmentMutation();
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

	const handleBack = () => {
		navigate(`/${ApplicationRoutes.ADMIN_UTIL_BASE}/${ApplicationRoutes.SERVICE_REQUEST_MODULE}/${ApplicationRoutes.DEPARTMENT_CONFIGURATION}`);
	};

	const getInitials = (firstName: string, lastName: string) => {
		return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
	};

	const getAvatarColor = (email: string) => {
		const colors = ["#EF4444", "#EC4899", "#10B981", "#6B7280", "#3B82F6", "#F59E0B", "#8B5CF6", "#14B8A6"];
		const index = email.charCodeAt(0) % colors.length;
		return colors[index];
	};

	const handleRemoveMember = async (userId: string) => {
		try {
			await removeStaff({
				departmentId: id || "",
				userId: userId,
			}).unwrap();

			toast.success("Member removed successfully!");
			refetch();
		} catch (error: any) {
			toast.error(error?.data?.message || "Failed to remove member. Please try again.");
		}
	};

	const handleDeleteDepartment = async () => {
		if (!id) {
			return;
		}

		try {
			await deleteDepartment(id).unwrap();
			toast.success("Department deleted successfully!");
			setIsDeleteModalOpen(false);
			navigate(`/${ApplicationRoutes.ADMIN_UTIL_BASE}/${ApplicationRoutes.SERVICE_REQUEST_MODULE}/${ApplicationRoutes.DEPARTMENT_CONFIGURATION}`);
		} catch (error: any) {
			toast.error(error?.data?.message || "Failed to delete department. Please try again.");
		}
	};

	if (isLoading) {
		return (
			<div className={styles.container}>
				<div style={{ textAlign: 'center', padding: '2rem' }}>Loading department details...</div>
			</div>
		);
	}

	if (error || !department) {
		return (
			<div className={styles.container}>
				<div style={{ textAlign: 'center', padding: '2rem', color: 'red' }}>
					Error loading department details. Please try again.
				</div>
				<div style={{ textAlign: 'center', marginTop: '1rem' }}>
					<Button text="Go Back" variant="secondary" onClick={handleBack} />
				</div>
			</div>
		);
	}

	return (
		<div className={styles.container}>
			<div className={styles.header}>
				<h1 className={styles.title}>{department.name}</h1>
			</div>

			<div className={styles.content}>
				<div className={styles.section}>
					<h2 className={styles.sectionTitle}>Description</h2>
					<p className={styles.description}>{department.description}</p>
					<Can permission={UtilityPermissions.DeleteDepartment}>
						<button
							className={styles.deleteDepartmentButton}
							onClick={() => setIsDeleteModalOpen(true)}
							disabled={isDeleting}
						>
							<span>🗑</span>
							<span>Delete Department</span>
						</button>
					</Can>
				</div>

				<div className={styles.section}>
					<h2 className={styles.sectionTitle}>Supervisor</h2>
					<div className={styles.supervisorCard}>
						<div className={styles.userInfo}>
							<div
								className={styles.avatar}
								style={{ backgroundColor: getAvatarColor(department.supervisor.email) }}
							>
								{getInitials(department.supervisor.firstName, department.supervisor.lastName)}
							</div>
							<div className={styles.userDetails}>
								<div className={styles.userName}>
									{department.supervisor.firstName} {department.supervisor.lastName}
								</div>
								<div className={styles.userEmail}>{department.supervisor.email}</div>
							</div>
						</div>
					</div>
				</div>

				<div className={styles.section}>
					<div className={styles.sectionHeader}>
						<h2 className={styles.sectionTitle}>Members ({department.staff.length})</h2>
						<Can permission={UtilityPermissions.EditDepartment}>
							<Button
								text="+ Add Member"
								variant="primary"
								onClick={() => setIsAddMembersModalOpen(true)}
							/>
						</Can>
					</div>
					<div className={styles.membersList}>
						{department.staff.map((member) => (
							<div key={member.id} className={styles.memberCard}>
								<div className={styles.userInfo}>
									<div
										className={styles.avatar}
										style={{ backgroundColor: getAvatarColor(member.email) }}
									>
										{getInitials(member.firstName, member.lastName)}
									</div>
									<div className={styles.userDetails}>
										<div className={styles.userName}>
											{member.firstName} {member.lastName}
										</div>
										<div className={styles.userEmail}>{member.email}</div>
									</div>
								</div>
								<Can permission={UtilityPermissions.EditDepartment}>
									<button
										className={styles.deleteButton}
										onClick={() => handleRemoveMember(member.id)}
										disabled={isRemoving}
									>
										<span>🗑</span>
									</button>
								</Can>
							</div>
						))}
					</div>
				</div>
			</div>

			{isAddMembersModalOpen && (
				<AddMembersModal
					departmentId={id || ""}
					existingStaffIds={[
						department.supervisor.id,
						...department.staff.map(member => member.id)
					]}
					onClose={() => setIsAddMembersModalOpen(false)}
					onSuccess={() => refetch()}
				/>
			)}

			<ConfirmationModal
				isOpen={isDeleteModalOpen}
				onClose={() => setIsDeleteModalOpen(false)}
				onConfirm={handleDeleteDepartment}
				title="Delete Department"
				message={`Are you sure you want to delete "${department.name}"? This action cannot be undone.`}
				confirmText="Delete"
				cancelText="Cancel"
				isLoading={isDeleting}
				variant="danger"
			/>
		</div>
	);
});

export default DepartmentDetailPage;
